import React, { useState, useEffect, useRef } from 'react';
import IosCard from '../../components/iOSCard';
import { db, storage } from '../../firebase'; 
import { 
    collection, addDoc, getDocs, query, orderBy, deleteDoc, doc, updateDoc, serverTimestamp 
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { 
    VideoCameraIcon, CloudArrowUpIcon, TrashIcon, CheckCircleIcon,
    PlayIcon, DocumentTextIcon, PencilSquareIcon, XMarkIcon,
    LockClosedIcon, LockOpenIcon, ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
// ✅ استيراد الدالة البارونية للإرسال المركزي
import { sendNotification } from '../../firebase'; 

const LessonsManager = ({ user }) => {
    const [courseType, setCourseType] = useState('كورس أول');
    const [selectedChapter, setSelectedChapter] = useState('الفصل الأول');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    // [MODERN_GLASS_ALERTS] - الحالات الجديدة للمودالات الزجاجية لضمان عدم خروج النظام عن الستايل
    const [customAlert, setCustomAlert] = useState({ show: false, msg: "", type: "info" });

    const [lessonTitle, setLessonTitle] = useState("");
    const [description, setDescription] = useState("");
    
    const [videoFile, setVideoFile] = useState(null);
    const [pdfFile, setPdfFile] = useState(null);
    const videoInputRef = useRef(null);
    const pdfInputRef = useRef(null);

    const [lessons, setLessons] = useState([]);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedLesson, setSelectedLesson] = useState(null);

    useEffect(() => {
        const fetchLessons = async () => {
            try {
                const q = query(collection(db, "lessons"), orderBy("createdAt", "desc"));
                const querySnapshot = await getDocs(q);
                const lessonsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setLessons(lessonsData);
            } catch (error) {
                console.error("Error fetching lessons:", error);
                // استبدال الـ alert بنظام التنبيه الملكي
                triggerAlert("فشل في جلب البيانات يا غالي ❌", "error");
            }
        };
        fetchLessons();
    }, []);

    // [SECTION: ALERT_TRIGGER] - دالة إطلاق التنبيهات الزجاجية
    const triggerAlert = (msg, type = "info") => {
        setCustomAlert({ show: true, msg, type });
        if (type !== 'error') {
            setTimeout(() => setCustomAlert({ show: false, msg: "", type: "info" }), 3000);
        }
    };

    // [SECTION: TOGGLE_LOCK_LOGIC] - نظام القفل الموحد لضمان الاستجابة الفورية عند الطالب
    const toggleLessonLock = async (lesson) => {
        try {
            const isLocking = !lesson.isLocked; 
            const lessonRef = doc(db, "lessons", lesson.id);
            
            // ✅ التعديل الذهبي الموحد: تحديث الحقلين معاً (isLocked و status)
            const updateData = { 
                isLocked: isLocking,
                status: isLocking ? "locked" : "active" 
            };

            await updateDoc(lessonRef, updateData);
            
            setLessons(lessons.map(l => l.id === lesson.id ? { ...l, ...updateData } : l));
            
            triggerAlert(
                isLocking ? "تم قفل المحاضرة عن الطلاب 🔒" : "تم فتح المحاضرة للجميع 🔓 بنجاح", 
                "success"
            );
        } catch (error) {
            triggerAlert("فشل في تغيير حالة القفل، تأكد من الاتصال 🌐", "error");
        }
    };

    const openEditModal = (lesson) => {
        setSelectedLesson(lesson);
        setLessonTitle(lesson.title);
        setDescription(lesson.desc || "");
        setCourseType(lesson.courseType);
        setSelectedChapter(lesson.chapter);
        setIsEditModalOpen(true);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            const lessonRef = doc(db, "lessons", selectedLesson.id);
            await updateDoc(lessonRef, {
                title: lessonTitle,
                desc: description,
                courseType: courseType,
                chapter: selectedChapter
            });
            
            setLessons(lessons.map(l => l.id === selectedLesson.id ? 
                {...l, title: lessonTitle, desc: description, courseType, chapter: selectedChapter} : l));
            
            setIsEditModalOpen(false);
            setShowSuccessModal(true);
            setTimeout(() => setShowSuccessModal(false), 2000);
            resetForm();
        } catch (error) {
            triggerAlert("فشل التعديل: " + error.message, "error");
        }
    };

    const confirmDelete = (lesson) => {
        setSelectedLesson(lesson);
        setIsDeleteModalOpen(true);
    };

    const handleDelete = async () => {
        try {
            await deleteDoc(doc(db, "lessons", selectedLesson.id));
            setLessons(lessons.filter(l => l.id !== selectedLesson.id));
            setIsDeleteModalOpen(false);
            triggerAlert("تم حذف المحاضرة بنجاح 🗑️", "success");
        } catch (error) {
            triggerAlert("فشل الحذف: " + error.message, "error");
        }
    };

    const resetForm = () => {
        setLessonTitle("");
        setDescription("");
        setVideoFile(null);
        setPdfFile(null);
        setSelectedLesson(null);
        if (videoInputRef.current) videoInputRef.current.value = "";
        if (pdfFile) pdfInputRef.current.value = ""; // تم التصحيح ليكون pdfInputRef
    };

    // [SECTION: الإصلاح الباروني لدالة الرفع]
const handleUpload = async (e) => {
    e.preventDefault();
    if (!lessonTitle || !videoFile) return triggerAlert("يا غالي، لازم تختار عنوان وفيديو! ⚠️", "info");

    setIsUploading(true);
    setUploadProgress(0);
    
    try {
        const videoRef = ref(storage, `lessons/videos/${Date.now()}-${videoFile.name}`);
        const videoUploadTask = uploadBytesResumable(videoRef, videoFile);

        videoUploadTask.on('state_changed', (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(Math.round(progress));
        });

        await videoUploadTask;
        const videoUrl = await getDownloadURL(videoUploadTask.snapshot.ref);

        let pdfUrl = "";
        if (pdfFile) {
            const pdfRef = ref(storage, `lessons/pdfs/${Date.now()}-${pdfFile.name}`);
            await uploadBytesResumable(pdfRef, pdfFile);
            pdfUrl = await getDownloadURL(pdfRef);
        }

        const newLesson = {
            title: lessonTitle,
            url: videoUrl,
            pdfUrl: pdfUrl,
            chapter: selectedChapter,
            courseType: courseType,
            instructor: "الأستاذة نور عماد",
            desc: description || "مدخل شامل لعالم الأحياء",
            createdAt: serverTimestamp(),
            status: "active", 
            isLocked: false,
            isPublic: true,
            studentsCount: 0
        };

        const docRef = await addDoc(collection(db, "lessons"), newLesson);

        // ✅ التنبيه الباروني العام
        await addDoc(collection(db, "notifications"), {
            message: "📺 تم رفع محاضرة جديدة: " + lessonTitle,
            text: "محاضرة جديدة بانتظارك، ادخل للمنصة الآن!",
            createdAt: serverTimestamp(),
            type: 'video',
            targetId: 'all',
            isRead: false,
            role: 'student'
        });

        setShowSuccessModal(true);
        setLessons([{ id: docRef.id, ...newLesson }, ...lessons]);
        resetForm();
        setTimeout(() => setShowSuccessModal(false), 2000);

    } catch (error) {
        // [ALBARON_SECURITY_CHECK] - التقاط الخطأ هنا
        triggerAlert("يا ضلع، فشل الرفع: " + error.message, "error");
        console.error("خطأ الرفع:", error);
    } finally {
        // [FINALIZATION] - إنهاء العملية سواء نجحت أو فشلت
        setIsUploading(false);
    }
};
    return (
        <div style={{ fontFamily: "'Cairo', sans-serif" }} className="space-y-8 animate-in fade-in duration-700 h-full flex flex-col text-right font-cairo" dir="rtl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 shrink-0">
                <IosCard title="رفع محتوى جديد" icon={<CloudArrowUpIcon className="w-6 h-6" />} color="blue">
                    <form onSubmit={handleUpload} className="space-y-4">
                        <input required type="text" value={lessonTitle} onChange={(e) => setLessonTitle(e.target.value)} placeholder="اسم المحاضرة..." className="w-full p-4 rounded-2xl ios-glass border-none outline-none font-bold bg-white/50 dark:bg-black/20 dark:text-white" />
                        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="وصف المحاضرة..." className="w-full p-4 rounded-2xl ios-glass border-none outline-none font-bold h-24 bg-white/50 dark:bg-black/20 dark:text-white" />
                        <div className="grid grid-cols-2 gap-4">
                            <button type="button" onClick={() => videoInputRef.current.click()} className={`p-4 rounded-2xl border-2 border-dashed transition-all active:scale-95 ${videoFile ? 'border-emerald-400 text-emerald-600 bg-emerald-50/30' : 'border-gray-200 text-gray-400 hover:border-blue-400'}`}>
                                <VideoCameraIcon className="w-6 h-6 mx-auto" />
                                <span className="text-[10px] font-black block mt-1">{videoFile ? "فيديو جاهز" : "اختر فيديو"}</span>
                                <input type="file" hidden ref={videoInputRef} onChange={(e) => setVideoFile(e.target.files[0])} accept="video/*" />
                            </button>
                            <button type="button" onClick={() => pdfInputRef.current.click()} className={`p-4 rounded-2xl border-2 border-dashed transition-all active:scale-95 ${pdfFile ? 'border-purple-400 text-purple-600 bg-purple-50/30' : 'border-gray-200 text-gray-400 hover:border-purple-400'}`}>
                                <DocumentTextIcon className="w-6 h-6 mx-auto" />
                                <span className="text-[10px] font-black block mt-1">{pdfFile ? "ملزمة جاهزة" : "اختر ملزمة"}</span>
                                <input type="file" hidden ref={pdfInputRef} onChange={(e) => setPdfFile(e.target.files[0])} accept=".pdf" />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <select value={courseType} onChange={(e) => setCourseType(e.target.value)} className="p-4 rounded-2xl ios-glass outline-none font-bold cursor-pointer bg-white/50 dark:bg-black/40 dark:text-white">
                                <option className="bg-white dark:bg-[#1C1C1E]">كورس أول</option>
                                <option className="bg-white dark:bg-[#1C1C1E]">كورس ثاني</option>
                            </select>
                            <select value={selectedChapter} onChange={(e) => setSelectedChapter(e.target.value)} className="p-4 rounded-2xl ios-glass outline-none font-bold cursor-pointer bg-white/50 dark:bg-black/40 dark:text-white">
                                {courseType === 'كورس أول' ? <><option className="bg-white dark:bg-[#1C1C1E]">الفصل الأول</option><option className="bg-white dark:bg-[#1C1C1E]">الفصل الثاني</option><option className="bg-white dark:bg-[#1C1C1E]">الفصل الثالث</option></> : <><option className="bg-white dark:bg-[#1C1C1E]">الفصل الرابع</option><option className="bg-white dark:bg-[#1C1C1E]">الفصل الخامس</option></>}
                            </select>
                        </div>
                        {isUploading && (
                            <div className="w-full h-2 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                                <motion.div className="h-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]" initial={{ width: 0 }} animate={{ width: `${uploadProgress}%` }} />
                            </div>
                        )}
                        <button type="submit" disabled={isUploading} className="w-full py-4 bg-[#007AFF] text-white rounded-[22px] font-black shadow-lg shadow-blue-500/30 active:scale-[0.98] transition-all">
                            {isUploading ? `جاري الرفع ${uploadProgress}%` : "نشر المحاضرة الآن 🚀"}
                        </button>
                    </form>
                </IosCard>

                <IosCard title="المحاضرات المنشورة" icon={<PlayIcon className="w-6 h-6" />} color="purple">
                    <div className="custom-scroll overflow-y-auto max-h-[450px] space-y-3 px-2">
                        {lessons.map((lesson) => (
                            <div key={lesson.id} className={`ios-glass p-4 flex justify-between items-center rounded-2xl border border-white/20 shadow-sm transition-all ${lesson.isLocked ? 'bg-red-500/5 opacity-80 grayscale-[0.3]' : 'bg-white/40 dark:bg-white/5'}`}>
                                <div className="text-right">
                                    <h4 className="font-black text-gray-800 dark:text-white text-sm flex items-center gap-2">
                                        {lesson.title} {lesson.isLocked && <LockClosedIcon className="w-3 h-3 text-red-500 animate-pulse" />}
                                    </h4>
                                    <p className="text-[9px] text-gray-400 font-bold uppercase">{lesson.courseType} • {lesson.chapter}</p>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => toggleLessonLock(lesson)} className={`p-2 rounded-xl transition-all active:scale-90 ${lesson.isLocked ? 'text-red-500 bg-red-100/50 shadow-inner' : 'text-emerald-500 hover:bg-emerald-50'}`}>
                                        {lesson.isLocked ? <LockClosedIcon className="w-5 h-5" /> : <LockOpenIcon className="w-5 h-5" />}
                                    </button>
                                    <button onClick={() => openEditModal(lesson)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-xl transition-all active:scale-90"><PencilSquareIcon className="w-5 h-5" /></button>
                                    <button onClick={() => confirmDelete(lesson)} className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-all active:scale-90"><TrashIcon className="w-5 h-5" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </IosCard>
            </div>

            {/* [SECTION: MODALS] - المودالات التفاعلية والاشعارات */}
            <AnimatePresence>
                {/* مودال التنبيهات الزجاجي الملكي */}
                {customAlert.show && (
                    <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4 backdrop-blur-sm bg-black/40">
                        <motion.div initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 20 }} className="ios-glass p-8 rounded-[40px] shadow-2xl border border-white/30 max-w-sm w-full text-center bg-white/95 dark:bg-[#1C1C1E]/90 backdrop-blur-3xl">
                            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${customAlert.type === 'error' ? 'bg-red-100 text-red-500 dark:bg-red-500/20' : 'bg-blue-100 text-blue-500 dark:bg-blue-500/20'}`}>
                                {customAlert.type === 'error' ? <ExclamationCircleIcon className="w-10 h-10" /> : <CheckCircleIcon className="w-10 h-10" />}
                            </div>
                            <h3 className="text-xl font-black mb-2 dark:text-white font-cairo">{customAlert.type === 'error' ? 'حدث خطأ ما' : 'تنبيه ملكي'}</h3>
                            <p className="text-sm font-bold text-gray-500 dark:text-gray-300 mb-6 leading-relaxed px-2 font-cairo">{customAlert.msg}</p>
                            <button onClick={() => setCustomAlert({ ...customAlert, show: false })} className="w-full py-4 bg-[#007AFF] text-white rounded-2xl font-black active:scale-95 transition-all shadow-lg font-cairo">فهمت ضلعي</button>
                        </motion.div>
                    </div>
                )}

                {/* مودال النجاح الزجاجي المطور */}
                {showSuccessModal && (
                    <div className="fixed inset-0 z-[2000] flex items-center justify-center pointer-events-none">
                        <motion.div initial={{ scale: 0.8, opacity: 0, y: 50 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.8, opacity: 0 }} className="bg-white/90 dark:bg-[#1C1C1E]/90 backdrop-blur-xl p-10 rounded-[50px] shadow-2xl flex flex-col items-center gap-4 border border-white/20">
                            <CheckCircleIcon className="w-16 h-16 text-emerald-500" />
                            <h3 className="text-2xl font-black dark:text-white font-cairo">تم بنجاح!</h3>
                            <p className="text-sm font-bold text-gray-400 dark:text-gray-300 font-cairo">تم إشعار الطلاب بتحديثات المحاضرة فوراً 🚀</p>
                        </motion.div>
                    </div>
                )}

                {/* مودال التعديل */}
                {isEditModalOpen && (
                    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 backdrop-blur-md bg-black/40">
                        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="ios-glass p-8 max-w-lg w-full rounded-[40px] shadow-2xl border-white/30 border relative bg-white/95 dark:bg-[#1C1C1E]/95 backdrop-blur-3xl">
                            <div className="flex justify-between items-center mb-6 font-cairo">
                                <h3 className="text-xl font-black text-gray-800 dark:text-white">تعديل بيانات المحاضرة</h3>
                                <button onClick={() => {setIsEditModalOpen(false); resetForm();}} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-white"><XMarkIcon className="w-6 h-6" /></button>
                            </div>
                            <form onSubmit={handleUpdate} className="space-y-4 text-right">
                                <input required type="text" value={lessonTitle} onChange={(e) => setLessonTitle(e.target.value)} className="w-full p-4 rounded-2xl bg-gray-50/50 dark:bg-black/20 outline-none font-bold text-gray-700 dark:text-white border border-gray-100 dark:border-white/10" />
                                <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="w-full p-4 rounded-2xl bg-gray-50/50 dark:bg-black/20 outline-none font-bold text-gray-700 dark:text-white h-24 border border-gray-100 dark:border-white/10" />
                                <button type="submit" className="w-full py-4 bg-[#34C759] text-white rounded-2xl font-black shadow-lg active:scale-95 transition-all font-cairo">حفظ التعديلات ✨</button>
                            </form>
                        </motion.div>
                    </div>
                )}

                {/* مودال الحذف */}
                {isDeleteModalOpen && (
                    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 backdrop-blur-sm bg-black/40">
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="ios-glass p-8 rounded-[40px] shadow-2xl relative z-10 max-w-sm w-full text-center bg-white/95 dark:bg-[#1C1C1E]/95 backdrop-blur-3xl border border-white/20">
                            <div className="w-16 h-16 bg-red-100 dark:bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 text-red-500"><TrashIcon className="w-10 h-10" /></div>
                            <h3 className="text-xl font-black text-gray-800 dark:text-white mb-6 px-2 font-cairo">هل تريد حذف المحاضرة نهائياً؟</h3>
                            <div className="flex gap-3 font-cairo">
                                <button onClick={handleDelete} className="flex-1 py-4 bg-red-500 text-white rounded-2xl font-black active:scale-95 transition-all">نعم، احذف</button>
                                <button onClick={() => setIsDeleteModalOpen(false)} className="flex-1 py-4 bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white rounded-2xl font-black active:scale-95 transition-all">إلغاء</button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default LessonsManager;